import * as React from 'react'

import { toast } from '@/lib/toasts/api'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toExchangeRateDate } from './allowancesSyncHelpers'

export {
  affectsAllowances,
  eventsAffectAllowances,
  toExchangeRateDate,
} from './allowancesSyncHelpers'

export type GeneratedAllowances = {
  allowances: any[]
  invoices: any[]
}

export type GenerateAllowancesOptions = {
  /**
   * Keep the "Zwrot kosztów noclegu" rows and rebuild only "Sanitariaty".
   * Mirrors WPF, where an event change runs RefreshTripParts() ->
   * RefreshSanitarityInvoices(); the accommodation invoice follows the
   * accommodation list, not the events.
   */
  preserveAccommodationInvoices?: boolean
  /** Skip the success toast (used by the automatic recalculation). */
  silent?: boolean
}

const AUTO_RECALCULATION_DELAY_MS = 600

/**
 * Keeps the diets ("Diety") and the auto-generated cost invoices ("Koszty") in
 * sync with the trip events, the way the WPF TripViewModel does: every event
 * change recalculates, instead of waiting for the user to open a particular
 * tab. `flushPending` lets the save path wait for a recalculation that is still
 * debounced or in flight, so what gets saved is what the user sees.
 */
export function useBusinessTripAllowancesSync({
  companyId,
  driverId,
  formDataRef,
  setFormData,
}: {
  companyId: string
  driverId: string
  formDataRef: React.MutableRefObject<any>
  setFormData: (updater: any) => void
}) {
  const { generateAllowances } = useBusinessTripService()
  const { t } = useLocalize()

  const generateAllowancesRef = React.useRef(generateAllowances)
  generateAllowancesRef.current = generateAllowances
  const translateRef = React.useRef(t)
  translateRef.current = t
  const setFormDataRef = React.useRef(setFormData)
  setFormDataRef.current = setFormData

  const pendingRef = React.useRef<GenerateAllowancesOptions | null>(null)
  const runningRef = React.useRef<Promise<GeneratedAllowances | null> | null>(
    null,
  )
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const runGenerate = React.useCallback(
    async (
      options: GenerateAllowancesOptions,
    ): Promise<GeneratedAllowances | null> => {
      const formData = formDataRef.current || {}
      try {
        const result = await generateAllowancesRef.current({
          companyId,
          driverId,
          exchangeRateDate: toExchangeRateDate(formData.exchangeRateDate),
          country: formData.destinationCountry || null,
          events: formData.events || [],
          alimentations: formData.alimentations || [],
          invoices: formData.invoices || [],
          // formData carries both spellings: `accomodations` is the untouched copy
          // from the API payload, `accommodations` is the one the UI updates.
          // Read the live one — same precedence as the save path.
          accomodations:
            formData.accommodations || formData.accomodations || [],
          stopDownloadingRates: formData.stopDownloadingRates || false,
          preserveAccommodationInvoices:
            options.preserveAccommodationInvoices ?? false,
        })

        if (!result) return null

        const allowances = result.Allowances || result.allowances || []
        const invoices = result.Invoices || result.invoices || []

        setFormDataRef.current((prev: any) => ({
          ...prev,
          allowances,
          invoices,
        }))

        if (!options.silent) {
          toast.success({
            text: translateRef.current('businessTrip.allowances-generated'),
          })
        }

        return { allowances, invoices }
      } catch (err: any) {
        console.error('Error generating allowances:', err)
        toast.error({
          text:
            err?.response?.data?.message ||
            translateRef.current(
              'newDelegation.delegation-edit-failed-details',
            ),
        })
        return null
      }
    },
    [companyId, driverId, formDataRef],
  )

  /**
   * Runs everything that is queued or already running and resolves with the
   * result of the last recalculation (null when there was nothing to do).
   */
  const flushPending =
    React.useCallback(async (): Promise<GeneratedAllowances | null> => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      let last: GeneratedAllowances | null = null
      while (runningRef.current || pendingRef.current) {
        if (runningRef.current) {
          last = await runningRef.current
          continue
        }

        const options = pendingRef.current as GenerateAllowancesOptions
        pendingRef.current = null
        const promise = runGenerate(options)
        runningRef.current = promise
        try {
          last = await promise
        } finally {
          if (runningRef.current === promise) runningRef.current = null
        }
      }

      return last
    }, [runGenerate])

  /** Queue a silent recalculation after the user stopped editing events. */
  const scheduleRecalculation = React.useCallback(() => {
    const queued = pendingRef.current
    pendingRef.current = {
      silent: true,
      preserveAccommodationInvoices: true,
      // An explicit action already waiting in the queue ("Generuj Koszty")
      // must not be downgraded to a silent, accommodation-preserving run —
      // it would never rebuild those invoices and never report back.
      ...(queued && !queued.silent ? queued : {}),
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      void flushPending()
    }, AUTO_RECALCULATION_DELAY_MS)
  }, [flushPending])

  /** Recalculate right now ("Generuj diety" / "Generuj Koszty"). */
  const regenerateNow = React.useCallback(
    (options: GenerateAllowancesOptions = {}) => {
      pendingRef.current = options
      return flushPending()
    },
    [flushPending],
  )

  return { scheduleRecalculation, regenerateNow, flushPending }
}
